import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import BannerContainer from "web/react/components/banner-container/banner-container";
import { BackToTopButton } from "web/react/components/buttons/back-to-top-button/back-to-top-button";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import { FeedProductCard } from "web/react/components/feed-grid/feed-grid";
import { FeedHeader } from "web/react/components/feed-header/feed-header";
import { ProductOverlay } from "web/react/components/product-overlay";
import { Rail } from "web/react/components/rail/rail";
import { ShopfrontMediaSlot } from "web/react/components/shopfront-media-slot";
import { StorefrontDescription } from "web/react/components/storefront-description";
import { StorefrontModal } from "web/react/components/storefront-modal";
import { StorefrontInfo } from "web/react/components/storefront-modal/storefront-info";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { FeedContextProvider } from "web/react/pages/feed/feed.context";
import { FilterBar } from "web/react/pages/feed/filter-bar";
import { useShopfrontSectionImpression } from "web/react/pages/feed/shopfront-feed/use-shopfront-section-impression";
import { FeedPage } from "web/react/pages/feed/utils";
import { setFeedBreadcrumbs } from "web/redux/ducks/feed-breadcrumbs";
import store, { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import {
    FeedHeaderSerializer,
    FeedNavigationSerializer,
    ProductFeedSerializer,
    ShopfrontSerializer,
} from "web/types/serializers";
import { UniversalFiltersData } from "web/types/universal-filters";
import { ShopfrontFeedGrid } from "./shopfront-feed-grid";
import * as styles from "./shopfront-feed.css";

export interface ShopfrontFeedProps {
    shopfront: ShopfrontSerializer;
    header: FeedHeaderSerializer;
    universalFilters: UniversalFiltersData;
    productFeed: ProductFeedSerializer;
    feedNavigation: FeedNavigationSerializer;
}

export function ShopfrontFeed({
    shopfront,
    header,
    universalFilters,
    productFeed,
    feedNavigation,
}: ShopfrontFeedProps): React.ReactElement {
    const overlayRef = useRef(null);
    const { isMobileViewport } = useDomViewport();
    const feedPageType = environment.get("feedType") as FeedPage;
    const [isInfoModalOpen, setInfoModalOpen] = useState(false);

    useEffect(() => {
        store.dispatch(setFeedBreadcrumbs(feedNavigation?.shop_breadcrumbs));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const feedCountState = useSelector((state: ReduxStoreState) => state.feedCount);
    const productCount = feedCountState.product_count ?? productFeed.feed_count?.product_count ?? 0;
    const retailerCount =
        feedCountState.retailer_count ?? productFeed.feed_count?.retailer_count ?? 0;

    const feedHeaderState = useSelector((state: ReduxStoreState) => state.feedHeader);
    const feedHeader = feedHeaderState.gender ? feedHeaderState : header;

    // Shopfronts are always RETAILER_PROFILE feeds, so the title is always a plain
    // string — no need for the generic getFeedTitle helper (SEARCH/RELATED_PRODUCTS/
    // DYNAMIC_FEED formatting doesn't apply here)
    const retailerName = feedHeader.title ?? "";
    const description = feedHeader.description;
    const shortDescription = feedHeader.short_description;

    const descriptionButtonRef = useShopfrontSectionImpression<HTMLDivElement>(
        "retailer_description_button"
    );
    const filterPillRef = useShopfrontSectionImpression<HTMLDivElement>("filter-pill");

    const hasScrolledFilters = useRef(false);
    const onFilterScroll = useCallback(() => {
        if (!hasScrolledFilters.current) {
            hasScrolledFilters.current = true;
            analytics.event({ category: "filter", action: "scrolled", truncate: false });
        }
    }, []);

    const openInfoModal = useCallback(() => {
        setInfoModalOpen(true);
    }, []);

    const closeInfoModal = useCallback(() => {
        analytics.event({
            category: "retailer_description_exit",
            action: "clicked",
            truncate: false,
        });
        setInfoModalOpen(false);
    }, []);

    return (
        <FeedContextProvider>
            <BannerContainer />
            <CustomerCaptureOverlay />
            <div ref={overlayRef}>
                <ProductOverlay parentRef={overlayRef} pageType="feed" />
            </div>

            <div className={styles.gutter}>
                <div className={styles.contentWidth}>
                    <FeedHeader
                        title={retailerName}
                        profile={feedHeader}
                        shortDescription={shortDescription}
                        description={description}
                        type={feedPageType}
                        productCount={productCount}
                        retailerCount={retailerCount}
                        breadcrumbs={feedNavigation?.shop_breadcrumbs}
                        promotions={feedHeader.promotions}
                        isShopfront
                    />
                </div>
            </div>

            <div data-testid="shopfront.media_slot_1" className={styles.mediaSlot1}>
                <ShopfrontMediaSlot slot={shopfront.media_slot_1} />
            </div>

            {/* On mobile the design places the retailer description below media slot 1;
                the copy inside the header is CSS-hidden on mobile instead (SSR-safe) */}
            <div className={styles.mobileDescription}>
                <div className={styles.gutter}>
                    <div className={styles.contentWidth} ref={descriptionButtonRef}>
                        <StorefrontDescription
                            description={description || shortDescription}
                            beforeOnClick={openInfoModal}
                            retailer={retailerName}
                            displayAsPreview={true}
                            parentArea="header"
                            retailerNetwork={feedHeader.retailer_network}
                        />
                        <StorefrontModal
                            isOpen={isInfoModalOpen}
                            onClose={closeInfoModal}
                            title={retailerName}
                            isCheckout={!!feedHeader.checkout_verified}
                        >
                            <StorefrontInfo
                                dateAdded={feedHeader.date_added || ""}
                                description={description || shortDescription || null}
                                isCheckout={!!feedHeader.checkout_verified}
                                isOfficialStore={false}
                                bestShippingOption={feedHeader.best_shipping_option || ""}
                                shipsToCountry={feedHeader.ships_to_country || ""}
                                parentArea="header"
                                retailerNetwork={feedHeader.retailer_network}
                            />
                        </StorefrontModal>
                    </div>
                </div>
            </div>

            <FilterBar
                universalFilters={universalFilters}
                productCount={productCount}
                retailerCount={retailerCount}
                productFeed={productFeed}
                blurFeed={false}
                filterPillRef={filterPillRef}
                onFilterScroll={onFilterScroll}
            />

            <ShopfrontFeedGrid data={productFeed} feedType={feedPageType}>
                {shopfront.product_cards.length > 0 && (
                    <div className={styles.railSection}>
                        <div className={styles.gutter}>
                            <div className={styles.contentWidth}>
                                <Rail
                                    title={shopfront.rail_title}
                                    caption={shopfront.rail_caption}
                                    analyticsLabel="shopfront_brand_picks"
                                    listItemClassName={styles.railItem}
                                    variant="secondary"
                                >
                                    {shopfront.product_cards.map((product, index) => (
                                        <FeedProductCard
                                            key={product.id}
                                            product={product}
                                            feedType={FeedPage.RETAILER_PROFILE}
                                            productPosition={index + 1}
                                        />
                                    ))}
                                </Rail>
                            </div>
                        </div>
                    </div>
                )}
                {!!shopfront.media_slot_2.image_url && (
                    <div data-testid="shopfront-media-slot-2">
                        <ShopfrontMediaSlot
                            slot={shopfront.media_slot_2}
                            analyticsCategory="retailer_media_slot_1"
                        />
                    </div>
                )}
                {!!shopfront.media_slot_3.image_url && (
                    <div data-testid="shopfront-media-slot-3">
                        <ShopfrontMediaSlot
                            slot={shopfront.media_slot_3}
                            analyticsCategory="retailer_media_slot_2"
                        />
                    </div>
                )}
            </ShopfrontFeedGrid>

            {isMobileViewport && <BackToTopButton />}
        </FeedContextProvider>
    );
}
